import useAuthStore, { type NavItem } from '~/stores/auth';

/**
 * Check if the user profile has been loaded from the API.
 */
export function isProfileLoadedFromApi(): boolean {
   const authStore = useAuthStore();
   return !authStore.isFetchingUserProfile && authStore.user?.user?.email !== '';
}

/**
 * Find a nav item by key in the nav_items tree (top-level and children).
 */
function findNavItemInTree(navItems: NavItem[], key: string): NavItem | undefined {
   for (const item of navItems) {
      if (item.key === key) {
         return item;
      }
      if (item.children) {
         const child = item.children.find(c => c.key === key);
         if (child) {
            return child;
         }
      }
   }
   return undefined;
}

/**
 * Check access to a route gated by a site_settings feature flag rather than by a
 * nav item. Returns '/lms' to redirect, or undefined to allow access.
 *
 * Deliberately distinct from checkNavItemAccess. Hiding a menu entry from the admin
 * Navigation Order tab should not make the route itself unreachable when the feature
 * is enabled - the two are separate decisions. Use this for features that have their
 * own flag, and checkNavItemAccess for those whose only signal is nav visibility.
 *
 * Fails closed: anything other than an explicit true redirects, so an opt-in feature
 * stays shut when the flag is missing from the payload.
 */
export function checkFeatureFlagAccess(flag: string): string | undefined {
   const authStore = useAuthStore();
   const settings = authStore?.user?.site_settings as
      | Record<string, unknown>
      | undefined;

   if (settings?.[flag] !== true) {
      return '/lms';
   }
   return undefined;
}

/**
 * Check if a nav item is accessible based on nav_items tree.
 * Returns '/lms' to redirect, or undefined to allow access.
 */
export function checkNavItemAccess(key: string): string | undefined {
   const authStore = useAuthStore();
   const navItems = authStore?.user?.site_settings?.nav_items ?? [];
   const item = findNavItemInTree(navItems, key);

   if (!item || !item.is_visible) {
      return '/lms';
   }
   return undefined;
}
